import { moNum } from "@/lib/moNum";
import { printHtml } from "@/components/inventory/shelfPrint";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const num = (n) => Math.round(n).toLocaleString();
const fmtQty = (n) => Number(Math.round(n * 100) / 100).toLocaleString();
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const printConsumption = (consName, entry, unit) => {
  const orders = entry?.orders || [];
  const u = unit ? esc(unit) : "";
  const rows = orders.map((o) => `
    <tr>
      <td><b>${o.manual ? "Manual" : esc(moNum(o.mwo_number) || "—")}</b>${o.copacker_name ? ` <span class="tag">${esc(o.copacker_name)}</span>` : ""}</td>
      <td>${esc(o.date || "—")}</td>
      <td>${esc(o.product_name || "—")}<div class="sub">${esc([o.design_name, o.case_pack ? `${Math.round(o.case_pack)}pk` : null].filter(Boolean).join(" · ") || "—")}</div></td>
      <td>${esc(o.customer_name || "—")}</td>
      <td class="r">${esc(fmtQty(o.qty))}${o.unit && o.unit !== "count" && o.unit !== "units" ? ` ${esc(o.unit)}` : ""}</td>
    </tr>`).join("");
  const html = `<html><head><title>Consumption History — ${esc(consName)}</title><style>
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #0f172a; margin: 32px; font-size: 12px; }
    h1 { font-size: 20px; letter-spacing: -0.02em; margin: 0 0 2px; }
    .sub-h { color: #64748b; margin: 0 0 18px; }
    .stats { display: flex; gap: 10px; margin-bottom: 18px; }
    .stat { border: 1px solid #cbd5e1; padding: 8px 12px; min-width: 120px; }
    .stat .lbl { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; font-weight: 700; }
    .stat .val { font-size: 14px; font-weight: 700; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; }
    th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; background: #f1f5f9; border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; }
    td { border: 1px solid #e2e8f0; padding: 5px 8px; vertical-align: top; }
    td .sub { color: #64748b; font-size: 11px; }
    .r { text-align: right; white-space: nowrap; }
    .tag { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #6d28d9; background: #ede9fe; padding: 1px 5px; border-radius: 8px; }
    tfoot td { font-weight: 700; background: #f8fafc; }
    .printed { margin-top: 16px; color: #94a3b8; font-size: 10px; }
  </style></head><body>
    <h1>Consumption History</h1>
    <p class="sub-h">${esc(consName)} — consumed by completed production runs</p>
    <div class="stats">
      <div class="stat"><div class="lbl">Total Consumed</div><div class="val">${num(entry?.consumed || 0)}${u}</div></div>
      <div class="stat"><div class="lbl">Last 30 Days</div><div class="val">${num(entry?.consumed_30d || 0)}${u}</div></div>
      <div class="stat"><div class="lbl">Production Runs</div><div class="val">${num(entry?.runs || 0)}</div></div>
      <div class="stat"><div class="lbl">Last Production</div><div class="val">${esc(entry?.last_date || "—")}</div></div>
    </div>
    <table>
      <thead><tr><th>MO</th><th>Production Date</th><th>Product / Design</th><th>Produced For</th><th class="r">Consumed</th></tr></thead>
      <tbody>${rows || `<tr><td colspan="5" style="text-align:center;color:#94a3b8;">No production has consumed this yet.</td></tr>`}</tbody>
      <tfoot><tr><td colspan="4">Total consumed</td><td class="r">${num(entry?.consumed || 0)}${u}</td></tr></tfoot>
    </table>
    <p class="printed">Printed ${new Date().toLocaleString()}</p>
  </body></html>`;
  printHtml(html);
};

// Past-production twin of ReservationsDialog: every completed run that consumed this variant.
export function ConsumptionDialog({ consKey, consName, consumption, onClose }) {
  const entry = consKey ? consumption[consKey] : null;
  const orders = entry?.orders || [];
  const unit = entry?.unit && entry.unit !== "count" && entry.unit !== "units" ? ` ${entry.unit}` : "";
  const stat = (label, value, testid, alwaysDark = false) => (
    <div className="border border-slate-200 px-3 py-2" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
      <p className={`mt-0.5 tabular-nums text-sm font-bold ${alwaysDark ? "text-slate-900" : "text-slate-700"}`}>{value}</p>
    </div>
  );
  return (
    <Dialog open={!!consKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm sm:max-w-4xl" data-testid="consumption-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 font-heading tracking-tight">
            Consumption History
            <Button type="button" variant="outline" size="sm" onClick={() => printConsumption(consName, entry, unit)}
              className="mr-5 rounded-sm text-xs font-semibold" data-testid="consumption-print-button">
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
          </DialogTitle>
          <DialogDescription>{consName} — consumed by completed production runs.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="consumption-stats">
          {stat("Total Consumed", `${num(entry?.consumed || 0)}${unit}`, "cons-stat-total", true)}
          {stat("Last 30 Days", `${num(entry?.consumed_30d || 0)}${unit}`, "cons-stat-30d", true)}
          {stat("Production Runs", num(entry?.runs || 0), "cons-stat-runs")}
          {stat("Last Production", entry?.last_date || "—", "cons-stat-last")}
        </div>
        {orders.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No production has consumed this yet.</p>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="consumption-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.08em] text-slate-500">
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">MO</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Production Date</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Product / Design</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Produced For</th>
                <th className="border-b border-slate-200 px-2 py-2 text-right font-semibold">Consumed</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={`${o.mwo_number || "run"}-${o.date}-${i}`} className="text-slate-700" data-testid={`consumption-order-${i}`}>
                  <td className="border-b border-slate-100 px-2 py-2 font-semibold text-slate-900 whitespace-nowrap">
                    {o.manual ? (
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-500" data-testid={`consumption-manual-${i}`}>Manual</span>
                    ) : (
                      moNum(o.mwo_number) || "—"
                    )}
                    {o.copacker_name && (
                      <span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700">{o.copacker_name}</span>
                    )}
                  </td>
                  <td className="border-b border-slate-100 px-2 py-2 whitespace-nowrap">{o.date || "—"}</td>
                  <td className="border-b border-slate-100 px-2 py-2">
                    <span className="block">{o.product_name || "—"}</span>
                    <span className="block text-xs text-slate-500">{[o.design_name, o.case_pack ? `${Math.round(o.case_pack)}pk` : null].filter(Boolean).join(" · ") || "—"}</span>
                  </td>
                  <td className="border-b border-slate-100 px-2 py-2" data-testid={`consumption-customer-${i}`}>{o.customer_name || "—"}</td>
                  <td className="border-b border-slate-100 px-2 py-2 text-right font-medium tabular-nums whitespace-nowrap">
                    {fmtQty(o.qty)}{o.unit && o.unit !== "count" && o.unit !== "units" ? ` ${o.unit}` : ""}
                  </td>
                </tr>
              ))}
              <tr className="text-slate-900">
                <td className="px-2 py-2 text-xs font-semibold uppercase tracking-wide" colSpan={4}>Total consumed</td>
                <td className="px-2 py-2 text-right font-bold tabular-nums whitespace-nowrap" data-testid="consumption-total">{num(entry?.consumed || 0)}{unit}</td>
              </tr>
            </tbody>
          </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
